/** Payroll CSV export constants (shared client/server). */

import { isZeroHourExportEntryType, isWithinEmployment } from './employeeTimeEntryHours';

export const PAYROLL_CSV_HEADERS = [
  'Datum',
  'Pers.-Nr.',
  'Kalenderschlüssel',
  'Stunden',
  'Tage',
  'Lohnart',
  'Abw. Faktor',
  'Kostenstelle',
  'Kostenträger',
] as const;

export const PAYROLL_ENTRY_TYPE_TO_KALENDERSCHLUESSEL: Record<string, number> = {
  work: 101,
  overtime_comp: 125,
  vacation: 117,
  sickness: 1,
  public_holiday: 118,
  unpaid_absence: 23,
  special_leave: 61,
  sickness_benefit: 2,
};

/** Agenda Lohnart per Kalenderschlüssel (only absence/holiday keys). */
export const PAYROLL_KALENDERSCHLUESSEL_TO_LOHNART: Record<number, number> = {
  1: 9,
  117: 1120,
  118: 1125,
};

export const PAYROLL_EXPORTABLE_ENTRY_TYPES = new Set(Object.keys(PAYROLL_ENTRY_TYPE_TO_KALENDERSCHLUESSEL));

export function kalenderschluesselForEntryType(entryType: string): number | null {
  return PAYROLL_ENTRY_TYPE_TO_KALENDERSCHLUESSEL[entryType] ?? null;
}

export function lohnartForKalenderschluessel(kalenderschluessel: number): number | null {
  return PAYROLL_KALENDERSCHLUESSEL_TO_LOHNART[kalenderschluessel] ?? null;
}

export type PayrollSourceEntry = {
  employee_id: string;
  entry_date: string;
  entry_type: string;
  start_time: string | null;
  end_time: string | null;
  break_minutes: number | null;
};

/** DB time entries plus synthetic public holidays from instance calendar. */
export function buildPayrollSourceEntriesForEmployee(
  employeeId: string,
  dbEntries: PayrollSourceEntry[],
  holidays: Array<{ holiday_date: string }>,
  from: string,
  to: string,
  employeeStartDate: string | null | undefined,
  employeeEndDate?: string | null,
): PayrollSourceEntry[] {
  const inRange = dbEntries.filter(
    (e) => e.employee_id === employeeId && e.entry_date >= from && e.entry_date <= to,
  );
  const existingDates = new Set(inRange.map((e) => e.entry_date));
  const virtualHolidays: PayrollSourceEntry[] = holidays
    .filter((h) => {
      if (h.holiday_date < from || h.holiday_date > to || existingDates.has(h.holiday_date)) return false;
      return isWithinEmployment(h.holiday_date, employeeStartDate, employeeEndDate);
    })
    .map((h) => ({
      employee_id: employeeId,
      entry_date: h.holiday_date,
      entry_type: 'public_holiday',
      start_time: null,
      end_time: null,
      break_minutes: 0,
    }));

  return [...inRange, ...virtualHolidays].sort((a, b) => a.entry_date.localeCompare(b.entry_date));
}

export function payrollDayFractionForHours(hours: number, expectedHoursPerDay: number): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  const expected =
    Number.isFinite(expectedHoursPerDay) && expectedHoursPerDay > 0 ? expectedHoursPerDay : 8;
  return Math.min(1, hours / expected);
}

/**
 * Split „Tage“ across export rows for one calendar day so they sum to at most 1.
 * Each row: Anteil = Stunden / Sollstunden; bei mehreren Zeilen am selben Tag
 * werden Anteile > 1 proportional skaliert (z. B. 4 h Urlaub + 4 h Arbeit → je 0,5).
 */
export function resolvePayrollExportTageForDay(
  rowHours: number[],
  expectedHoursPerDay: number,
): string[] {
  const fractions = rowHours.map((h) => payrollDayFractionForHours(h, expectedHoursPerDay));
  const total = fractions.reduce((a, b) => a + b, 0);
  if (total <= 0) return fractions.map(() => '');
  const scale = total > 1 ? 1 / total : 1;
  return fractions.map((f) => formatPayrollDays(Math.round(f * scale * 100) / 100));
}

export type PayrollExportTageRow = {
  employeeId: string;
  entryDate: string;
  entryType: string;
  stunden: number;
  tage: string;
};

export function assignPayrollExportTage(
  rows: PayrollExportTageRow[],
  expectedHoursByEmployeeId: Map<string, number>,
): void {
  const groups = new Map<string, PayrollExportTageRow[]>();
  for (const row of rows) {
    const key = `${row.employeeId}|${row.entryDate}`;
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }

  for (const group of groups.values()) {
    const expected = expectedHoursByEmployeeId.get(group[0].employeeId) ?? 8;
    const zeroHourRows = group.filter((r) => isZeroHourExportEntryType(r.entryType));
    const normalRows = group.filter((r) => !isZeroHourExportEntryType(r.entryType));

    zeroHourRows.forEach((row) => {
      row.tage = '1';
    });

    if (normalRows.length > 0) {
      const tageValues = resolvePayrollExportTageForDay(
        normalRows.map((r) => r.stunden),
        expected,
      );
      normalRows.forEach((row, i) => {
        row.tage = tageValues[i];
      });
    }
  }
}

export function mergeKalenderWorkRows<
  T extends { employeeId: string; entryDate: string; entryType: string; stunden: number },
>(rows: T[]): T[] {
  const out: T[] = [];
  const workIndex = new Map<string, number>();
  for (const row of rows) {
    if (row.entryType !== 'work') {
      out.push(row);
      continue;
    }
    const key = `${row.employeeId}|${row.entryDate}`;
    const existing = workIndex.get(key);
    if (existing == null) {
      workIndex.set(key, out.length);
      out.push({ ...row });
      continue;
    }
    const merged = out[existing]!;
    out[existing] = { ...merged, stunden: merged.stunden + row.stunden };
  }
  return out;
}

export function formatPayrollDays(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '';
  const rounded = Math.round(value * 100) / 100;
  if (rounded === 1) return '1';
  const s = rounded.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
  return s === '0' ? '' : s;
}

export function formatPayrollDateYmd(ymd: string): string {
  const [y, m, d] = String(ymd).slice(0, 10).split('-');
  if (!y || !m || !d) return ymd;
  return `${d}.${m}.${y}`;
}

/** Round export hours to 2 decimals without truncating to whole numbers. */
export function roundPayrollExportHours(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 100) / 100;
}

/** CSV „Stunden“: German decimal comma, up to 2 fractional digits (e.g. 7,8 / 7,85 / 8,00). */
export function formatPayrollHours(value: number, options?: { allowZero?: boolean }): string {
  if (options?.allowZero && Number.isFinite(value) && value === 0) return '0,00';
  const rounded = roundPayrollExportHours(value);
  if (rounded <= 0) return '';
  const [whole, frac = ''] = rounded.toFixed(2).split('.');
  const trimmedFrac = frac.replace(/0+$/, '');
  return trimmedFrac ? `${whole},${trimmedFrac}` : `${whole},00`;
}
